/**
 * ActionBar — Direction B rule 7: where a page's commit lives.
 *
 * Every page-level Save / Render / Generate in the product was a loose `<div className="row">`
 * somewhere down the page, and four surfaces grew their own (`.tpl-editor__actionbar`,
 * `.batch__actions`, `.settings__actions`, `.wizard__actions`). Direction B gives them one
 * bar on a top hairline, and re-states the One-Primary rule against it: **when a page has a
 * primary, the primary is in the bar.**
 *
 * The bar takes the primary as its own prop rather than as a child, for two reasons that are
 * really one: it can then place it last without the call site remembering to, and it can count
 * it.
 *
 * ## The One-Primary check NEVER THROWS (fix round 1, F-5)
 *
 * It used to, and that was wrong twice over.
 *
 *   · **It ran in production.** `app/App.tsx`'s per-route `<ErrorBoundary key={route}>` keeps
 *     the blast radius to one route's panel rather than the window — but a LAYOUT primitive
 *     taking a route out because a designer put two commits in a bar is worse than the defect
 *     it reports, and a user cannot fix it.
 *   · **It could not see the case it was written for.** It walked the React element tree for a
 *     literal `className`, so `<ActionBar primary={<SaveButton/>}>` — where `SaveButton`
 *     renders `.btn--primary` itself — put two primaries in the DOM and the guard stayed
 *     silent. It crashed on the trivial case and was blind to the realistic one.
 *
 * So it counts the RENDERED DOM through a ref, after mount, where a wrapped component is
 * visible; and it reports through `console.error`, once per bar, in DEV only. **`B4` is the
 * enforcement**: it counts `.btn--primary` per surface per viewport in a real browser across
 * every route, it cannot be fooled by composition either, and it fails a build rather than a
 * user's session.
 *
 * ## DEV is `import.meta.env.DEV` (fix round 2, N-2 / N-3)
 *
 * It was `typeof process !== "undefined" && process.env.NODE_ENV !== "production"`. Vite
 * substitutes `process.env.NODE_ENV` and **nothing else**, and `process` is undefined in a
 * browser — so the first operand short-circuited and the guard was `false` in every browser,
 * the dev server included. It ran only under vitest, where Node's `process` exists, which is
 * why four tests described behaviour a designer could never see. `import.meta.env.DEV` is the
 * flag the bundler owns: `true` in dev and under vitest, the literal `false` in a production
 * build. Wrapping the effect's body in it rather than testing it at the top is what lets the
 * bundler DELETE the block — `actionbar-production-bundle.test.ts` builds a fixture that mounts
 * this component and greps the bytes, because the shipped bundle is the only place that claim
 * can be checked.
 *
 * A bar with no primary is legitimate and stays silent — a read-only detail panel's bar holds
 * Close and nothing else, and (fix round 1, F-12) it no longer splits: the `margin-left: auto`
 * that pushes the commit to the end is on `.actionbar__primary`, which this component emits
 * only when it was given a `primary`. It used to be on `> :last-child:not(:only-child)`, which
 * cannot tell a primary from a secondary and shoved `[Close][Help]`'s Help hard right.
 *
 * The bar is `position: sticky; bottom: 0` in the shared layer (F-12 again): this JSDoc and
 * docs/design.md both call it "pinned to the bottom of the working column" and the CSS said
 * nothing, which is seven waves each inventing their own. In a column that does not scroll it
 * changes nothing.
 */
import { useEffect, useRef, type ReactNode } from "react";

export interface ActionBarProps {
  /** Names the group for a screen reader — "Template actions", "Run controls". */
  label: string;
  /** The ONE primary. Placed last, after `children`. Omit it when the page commits nothing. */
  primary?: ReactNode;
  /** Secondary and tertiary actions, in reading order. */
  children?: ReactNode;
}

export function ActionBar({ label, primary, children }: ActionBarProps) {
  const ref = useRef<HTMLDivElement>(null);
  const warned = useRef(false);
  // No dependency list: a bar that grows a second primary three renders in is the same defect
  // as one that mounted with two. `warned` is what keeps it to one line either way.
  //
  // `import.meta.env.DEV` is read INSIDE the effect and wraps the whole body (fix round 2,
  // N-2/N-3). Inside, because a suite has to be able to flip it; wrapping, because that is the
  // shape the bundler can delete — Vite substitutes the literal `false` in a production build
  // and the block, the `querySelectorAll` and the message go with it.
  useEffect(() => {
    if (import.meta.env.DEV) {
      if (warned.current || !ref.current) return;
      const n = ref.current.querySelectorAll(".btn--primary").length;
      if (n <= 1) return;
      warned.current = true;
      console.error(
        `ActionBar "${label}" rendered ${n} .btn--primary — a page has one primary, and it is ` +
          `the \`primary\` prop (Direction B rule 7). B4 fails the build on this.`,
      );
    }
  });
  return (
    <div className="actionbar" role="group" aria-label={label} ref={ref}>
      {children}
      {primary === undefined ? null : <span className="actionbar__primary">{primary}</span>}
    </div>
  );
}
